/**
 * React Query hooks for fetching and mutating workflow proposals.
 *
 * Endpoints (see `apps/api/src/workflows/routes/workflow-proposals.routes.ts`):
 *   GET    /workflows/:workflowId/proposals?status=pending
 *   GET    /workflows/:workflowId/proposals/:proposalId
 *   POST   /workflows/:workflowId/proposals/:proposalId/accept
 *   POST   /workflows/:workflowId/proposals/:proposalId/reject
 *
 * The accept endpoint supports optional `{ selection: { acceptedNodeIds } }`
 * for partial accepts — the server composes the final snapshot by merging the
 * current draft with the chosen subset of proposal changes.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { API_URL } from '../../../../config.js';
import { toApiError } from '../../../../lib/api-error.js';
import type { DiffSnapshot } from '@chatsdet/workflow-diff';
import type { Proposal, ProposalSummary } from './types';
import { addProposalApplyBreadcrumb } from './proposalApplyTelemetry';

interface ApiResponse<T> {
  ok: boolean;
  data?: T;
  error?: string;
}

function listKey(workflowId: string | null) {
  return ['workflow-proposals', 'list', workflowId] as const;
}

function detailKey(workflowId: string | null, proposalId: string | null) {
  return ['workflow-proposals', 'detail', workflowId, proposalId] as const;
}

/** List pending proposals for a workflow. */
export function usePendingProposals(
  workflowId: string | null,
): UseQueryResult<ProposalSummary[]> {
  return useQuery({
    queryKey: listKey(workflowId),
    queryFn: async () => {
      if (!workflowId) return [];
      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/proposals?status=pending`,
        { credentials: 'include' },
      );
      if (!res.ok) throw await toApiError(res, 'Failed to load proposals');
      const json: ApiResponse<ProposalSummary[]> = await res.json();
      return json.data ?? [];
    },
    enabled: Boolean(workflowId),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Create a new pending proposal from a snapshot. Used by the designer's
 * "Suggest changes" mode: the staged client edits are serialized into a
 * snapshot and submitted here as a `user`-sourced proposal. On success the
 * pending list is invalidated so the proposals pill/tray picks it up.
 *
 * The server aligns node ids against the live workflow and validates the
 * snapshot (ids must be UUIDs), so callers should sanitize any temp ids first.
 */
export function useCreateProposal(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      title: string;
      description?: string;
      snapshotJson: DiffSnapshot;
    }) => {
      if (!workflowId) throw new Error('Missing workflow ID');
      const res = await fetch(`${API_URL}/workflows/${workflowId}/proposals`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          title: params.title,
          description: params.description,
          snapshotJson: params.snapshotJson,
          source: 'user',
        }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to submit changes');
      return (await res.json()) as ApiResponse<Proposal>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listKey(workflowId) });
    },
  });
}

/** Fetch the full proposal including its snapshot. */
export function useProposal(
  workflowId: string | null,
  proposalId: string | null,
): UseQueryResult<Proposal | null> {
  return useQuery({
    queryKey: detailKey(workflowId, proposalId),
    queryFn: async () => {
      if (!workflowId || !proposalId) return null;
      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/proposals/${proposalId}`,
        { credentials: 'include' },
      );
      if (!res.ok) throw await toApiError(res, 'Failed to load proposal');
      const json: ApiResponse<Proposal> = await res.json();
      return json.data ?? null;
    },
    enabled: Boolean(workflowId && proposalId),
  });
}

/**
 * Apply a proposal — either in full (atomic) or with a per-node selection.
 *
 * `acceptedNodeIds === null` → send no body, the server applies the whole
 * proposal (same behavior as before partial accepts existed).
 * `acceptedNodeIds: string[]` → send `{ selection: { acceptedNodeIds } }`,
 * the server composes the final snapshot from the current draft plus the
 * chosen subset of changes.
 */
export function useApplyProposal(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      proposalId: string;
      acceptedNodeIds: string[] | null;
    }) => {
      if (!workflowId) throw new Error('Missing workflow ID');
      const telemetry = {
        workflowId,
        proposalId: params.proposalId,
        applyMode: params.acceptedNodeIds === null ? 'full' : 'partial',
        acceptedNodeCount: params.acceptedNodeIds?.length ?? null,
      } as const;
      const body =
        params.acceptedNodeIds === null
          ? undefined
          : JSON.stringify({
              selection: { acceptedNodeIds: params.acceptedNodeIds },
            });
      addProposalApplyBreadcrumb({ ...telemetry, phase: 'submit' });
      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/proposals/${params.proposalId}/accept`,
        {
          method: 'POST',
          credentials: 'include',
          headers: body ? { 'content-type': 'application/json' } : undefined,
          body,
        },
      );
      if (!res.ok) {
        addProposalApplyBreadcrumb({ ...telemetry, phase: 'api_error', status: res.status });
        throw await toApiError(res, 'Failed to apply proposal');
      }
      addProposalApplyBreadcrumb({ ...telemetry, phase: 'accepted', status: res.status });
      return (await res.json()) as ApiResponse<unknown>;
    },
    onSuccess: async (_data, variables) => {
      const telemetry = {
        workflowId,
        proposalId: variables.proposalId,
        applyMode: variables.acceptedNodeIds === null ? 'full' : 'partial',
        acceptedNodeCount: variables.acceptedNodeIds?.length ?? null,
      } as const;
      queryClient.invalidateQueries({ queryKey: ['workflow-proposals'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-snapshots', workflowId] });
      // Force the workflow-detail query to refetch *and await it* before
      // returning. The canvas's hydration effect won't re-run until new data
      // lands, and we don't want to exit review mode until the canvas is
      // ready to render the post-accept state. A refetch failure
      // (e.g. transient network blip) must NOT fail the whole mutation —
      // swallow it here so downstream onSuccess callers (toast + exit
      // review) still run; the canvas will self-heal on the next fetch.
      try {
        addProposalApplyBreadcrumb({ ...telemetry, phase: 'refetch_start' });
        await queryClient.refetchQueries({
          queryKey: ['workflow-detail', workflowId],
        });
        addProposalApplyBreadcrumb({ ...telemetry, phase: 'refetch_success' });
      } catch (error) {
        addProposalApplyBreadcrumb({
          ...telemetry,
          phase: 'refetch_error',
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        // Best-effort: the accept succeeded server-side, UI will catch up.
      }
    },
  });
}

/**
 * Revert an accepted proposal back to pending. Used by the undo-accept flow:
 * the canvas snapshot is restored (and synced) by the undo stack; this call
 * flips the proposal's status so it reappears in the pending list.
 */
export function useRevertProposal(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { proposalId: string }) => {
      if (!workflowId) throw new Error('Missing workflow ID');
      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/proposals/${params.proposalId}/revert`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw await toApiError(res, 'Failed to revert proposal');
      return (await res.json()) as ApiResponse<unknown>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-proposals'] });
    },
  });
}

/** Reject the whole proposal — doesn't touch the draft. */
export function useRejectProposal(workflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { proposalId: string }) => {
      if (!workflowId) throw new Error('Missing workflow ID');
      const res = await fetch(
        `${API_URL}/workflows/${workflowId}/proposals/${params.proposalId}/reject`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw await toApiError(res, 'Failed to reject proposal');
      return (await res.json()) as ApiResponse<unknown>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-proposals'] });
    },
  });
}
